/**
 * useBoardTaskDetailLevel - Hook for task detail level (row height).
 * Manages drag-to-resize, auto/manual mode, and 4 detail levels.
 * Writes detail state into boardLayoutStore when store is provided.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { loadString, saveString, setDragBodyStyles, stopDragBodyStyles } from '../../lib/dragResizeUtils';
import type { BoardLayoutStore } from '../components/boardLayoutStore';

// ============================================
// Types & Constants
// ============================================

export type TaskDetailLevel = 'minimal' | 'kompakt' | 'normal' | 'detailliert';

export const DETAIL_LEVELS: TaskDetailLevel[] = ['minimal', 'kompakt', 'normal', 'detailliert'];

export const ROW_HEIGHTS: Record<TaskDetailLevel, number> = {
  minimal: 14,
  kompakt: 22,
  normal: 28,
  detailliert: 40,
};

export const PADDINGS: Record<TaskDetailLevel, number> = {
  minimal: 1,
  kompakt: 2,
  normal: 2,
  detailliert: 3,
};

export const BORDER_RADII: Record<TaskDetailLevel, number> = {
  minimal: 2,
  kompakt: 3,
  normal: 4,
  detailliert: 6,
};

const DRAG_STEP = 14;
const HYSTERESIS = 4;

const TASK_DETAIL_DRAG_LEVEL_KEY = 'roadmap_2026_task_detail_drag_level';
const TASK_DETAIL_MODE_KEY = 'roadmap_2026_task_detail_mode';
const TASK_DETAIL_MANUAL_LEVEL_KEY = 'roadmap_2026_task_detail_manual_level';

// ============================================
// Helpers
// ============================================

function levelToIndex(level: TaskDetailLevel): number {
  return DETAIL_LEVELS.indexOf(level);
}

function indexToLevel(index: number): TaskDetailLevel {
  return DETAIL_LEVELS[Math.max(0, Math.min(3, index))];
}

// ============================================
// Initial state for store init
// ============================================

export function getInitialDetailState() {
  const dragLevel = loadString(TASK_DETAIL_DRAG_LEVEL_KEY, 'detailliert' as TaskDetailLevel, DETAIL_LEVELS);
  const mode = loadString(TASK_DETAIL_MODE_KEY, 'auto' as const, ['auto', 'manual']);
  const manualLevel = loadString(TASK_DETAIL_MANUAL_LEVEL_KEY, 'detailliert' as TaskDetailLevel, DETAIL_LEVELS);
  const detailLevel = mode === 'auto' ? dragLevel : manualLevel;
  return {
    detailLevel,
    isDetailResizing: false,
    rowHeight: ROW_HEIGHTS[detailLevel],
  };
}

// ============================================
// Hook
// ============================================

export interface UseTaskDetailLevelResult {
  detailLevel: TaskDetailLevel;
  rowHeight: number;
  padding: number;
  isResizing: boolean;
  handleResizeStart: (e: React.MouseEvent) => void;
  mode: 'auto' | 'manual';
  setLevel: (level: TaskDetailLevel) => void;
  setAutoMode: () => void;
}

export function useBoardTaskDetailLevel(store?: BoardLayoutStore): UseTaskDetailLevelResult {
  const [dragLevel, setDragLevel] = useState<TaskDetailLevel>(() => loadString(TASK_DETAIL_DRAG_LEVEL_KEY, 'detailliert' as TaskDetailLevel, DETAIL_LEVELS));
  const [mode, setMode] = useState<'auto' | 'manual'>(() => loadString(TASK_DETAIL_MODE_KEY, 'auto' as const, ['auto', 'manual']));
  const [manualLevel, setManualLevel] = useState<TaskDetailLevel>(() => loadString(TASK_DETAIL_MANUAL_LEVEL_KEY, 'detailliert' as TaskDetailLevel, DETAIL_LEVELS));
  const [isResizing, setIsResizing] = useState(false);

  const startYRef = useRef(0);
  const startLevelIndexRef = useRef(0);
  const dragLevelRef = useRef(dragLevel);
  const modeRef = useRef(mode);
  const isResizingRef = useRef(false);

  // Keep refs in sync
  useEffect(() => { dragLevelRef.current = dragLevel; }, [dragLevel]);
  useEffect(() => { modeRef.current = mode; }, [mode]);

  // Persist (skip during resize)
  useEffect(() => {
    if (isResizingRef.current) return;
    saveString(TASK_DETAIL_DRAG_LEVEL_KEY, dragLevel);
  }, [dragLevel]);

  useEffect(() => {
    saveString(TASK_DETAIL_MODE_KEY, mode);
  }, [mode]);

  useEffect(() => {
    saveString(TASK_DETAIL_MANUAL_LEVEL_KEY, manualLevel);
  }, [manualLevel]);

  // Derived values
  const detailLevel = mode === 'auto' ? dragLevel : manualLevel;
  const rowHeight = ROW_HEIGHTS[detailLevel];
  const padding = PADDINGS[detailLevel];

  // Sync to store when provided
  useEffect(() => {
    if (!store) return;
    store.send({
      type: 'detail.update',
      detailLevel,
      rowHeight,
      isDetailResizing: isResizing,
    });
  }, [store, detailLevel, rowHeight, isResizing]);

  // Resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    startYRef.current = e.clientY;
    startLevelIndexRef.current = levelToIndex(dragLevelRef.current);
    isResizingRef.current = true;
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    setDragBodyStyles('row-resize');

    const handleMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - startYRef.current;
      const currentIdx = levelToIndex(dragLevelRef.current);

      const idealIndex = Math.max(0, Math.min(3, startLevelIndexRef.current + Math.round(deltaY / DRAG_STEP)));

      if (idealIndex !== currentIdx) {
        const deltaFromStart = deltaY;
        const idealDelta = (idealIndex - startLevelIndexRef.current) * DRAG_STEP;
        let shouldSnap = false;

        if (idealIndex > currentIdx) {
          shouldSnap = deltaFromStart >= idealDelta - DRAG_STEP / 2 + HYSTERESIS;
        } else {
          shouldSnap = deltaFromStart <= idealDelta + DRAG_STEP / 2 - HYSTERESIS;
        }

        if (shouldSnap) {
          const newLevel = indexToLevel(idealIndex);
          dragLevelRef.current = newLevel;
          setDragLevel(newLevel);
        }
      }

      if (modeRef.current === 'manual') {
        modeRef.current = 'auto';
        setMode('auto');
      }
    };

    const handleMouseUp = () => {
      isResizingRef.current = false;
      setIsResizing(false);
      stopDragBodyStyles();
      saveString(TASK_DETAIL_DRAG_LEVEL_KEY, dragLevelRef.current);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      stopDragBodyStyles();
    };
  }, [isResizing]);

  const setLevel = useCallback((level: TaskDetailLevel) => {
    setMode('manual');
    setManualLevel(level);
  }, []);

  const setAutoMode = useCallback(() => {
    setMode('auto');
  }, []);

  return {
    detailLevel,
    rowHeight,
    padding,
    isResizing,
    handleResizeStart,
    mode,
    setLevel,
    setAutoMode,
  };
}
